import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import AttendanceDayCell from "../../components/attendance/AttendanceDayCell";
import AttendanceMonthCalendar from "../../components/attendance/AttendanceMonthCalendar";
import AttendanceLegend from "../../components/attendance/AttendanceLegend";
import { useApi } from "../../hooks/useApi";
import {
  attendanceRegister,
  attendanceDayView,
  attendanceStats,
  userAttendanceMonth,
  overrideDay,
  dayActivity,
  addPunchLog,
  updatePunchLog,
  deletePunchLog,
} from "../../api/attendanceService";
import { statusMeta, minutesLabel } from "../../utils/attendance";
import { nowIst, todayKeyIst, formatDate, formatDateTime } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";

/**
 * Admin attendance register: KPI strip + two tabs (colour-coded muster grid /
 * single-day view). Clicking a staff name opens their month calendar; clicking
 * any cell opens the day drawer with punch slots, admin corrections, a status
 * override, and the "edited by X at Y" activity timeline.
 */
export default function AttendanceRegisterPage() {
  const [tab, setTab] = useState("muster");
  const [month, setMonth] = useState(nowIst().format("YYYY-MM"));
  const [date, setDate] = useState(todayKeyIst());
  const [staffView, setStaffView] = useState(null); // {user_id, name} | null
  const [drawer, setDrawer] = useState(null); // {user:{id,name}, date} | null

  const stats = useApi(() => attendanceStats(month), [month]);
  const register = useApi(() => attendanceRegister(month), [month]);
  const dayApi = useApi(() => attendanceDayView(date), [date], { immediate: tab === "day" });

  const s = stats.data?.data;
  const registerRows = register.data?.data?.rows || [];
  const dayRows = dayApi.data?.data?.rows || [];
  const monthDates = useMemo(() => datesOfMonth(month), [month]);
  const todayKey = todayKeyIst();

  const openDrawer = (user, d) => setDrawer({ user, date: d });
  const reloadAll = () => {
    register.reload();
    stats.reload();
    if (tab === "day") dayApi.reload();
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Attendance</h1>
          <div className="page-sub">Color-coded register for the whole team. Click a cell to inspect or fix a day.</div>
        </div>
        <div className="page-actions">
          <Link className="btn btn-secondary" to="/admin/attendance/reports">
            <Icon name="file-text" size={14} /> Reports
          </Link>
          <Link className="btn btn-secondary" to="/admin/attendance/settings">
            <Icon name="settings" size={14} /> Settings
          </Link>
        </div>
      </div>

      {/* KPI strip */}
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <Kpi label="Present today" value={s?.today?.present} hint={`of ${s?.today?.total_staff ?? "—"} staff`} loading={stats.loading} />
        <Kpi label="On leave today" value={s?.today?.on_leave} loading={stats.loading} />
        <Kpi label="Not in yet" value={s?.today?.not_in_yet} loading={stats.loading} />
        <Kpi label="Late days (month)" value={s?.month?.late_days} loading={stats.loading} />
        <Kpi label="Missing checkouts" value={s?.month?.missing_checkouts} loading={stats.loading} />
        <Kpi label="Pending leaves" value={s?.month?.pending_leaves} loading={stats.loading} />
      </div>

      <div className="card">
        <div className="card-body">
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
            <div className="tabs">
              <button className={`tab ${tab === "muster" ? "active" : ""}`} onClick={() => { setTab("muster"); setStaffView(null); }}>
                Muster
              </button>
              <button className={`tab ${tab === "day" ? "active" : ""}`} onClick={() => { setTab("day"); dayApi.reload(); }}>
                Day view
              </button>
            </div>
            {tab === "muster" ? (
              <input type="month" className="input" style={{ width: 170 }} value={month}
                onChange={(e) => { if (e.target.value) { setMonth(e.target.value); setStaffView(null); } }} />
            ) : (
              <input type="date" className="input" style={{ width: 170 }} max={todayKey} value={date}
                onChange={(e) => e.target.value && setDate(e.target.value)} />
            )}
          </div>

          {tab === "muster" && !staffView && (
            register.loading ? (
              <LoadingBlock rows={5} />
            ) : registerRows.length === 0 ? (
              <EmptyState icon="users" title="No staff to track" hint="Add employees (or admins) and they'll appear here." />
            ) : (
              <>
                <div style={{ overflowX: "auto" }}>
                  <table className="table" style={{ minWidth: monthDates.length * 34 + 220 }}>
                    <thead>
                      <tr>
                        <th style={{ position: "sticky", left: 0, background: "var(--surface, #fff)", zIndex: 1 }}>Staff</th>
                        {monthDates.map((d) => (
                          <th key={d} style={{ textAlign: "center", padding: "6px 2px", fontWeight: d === todayKey ? 700 : 500 }}>
                            {Number(d.slice(8, 10))}
                          </th>
                        ))}
                        <th className="t-num">Hrs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registerRows.map((r) => (
                        <tr key={r.user_id}>
                          <td style={{ position: "sticky", left: 0, background: "var(--surface, #fff)", zIndex: 1, whiteSpace: "nowrap" }}>
                            <button className="btn-link t-primary fw-500" style={{ background: "none", border: 0, cursor: "pointer", padding: 0, font: "inherit" }}
                              title="Open month calendar"
                              onClick={() => setStaffView({ user_id: r.user_id, name: r.name })}>
                              {r.name}
                            </button>
                            <div className="t-muted text-xs">{r.role === "admin" ? "Admin" : r.department || "—"}</div>
                          </td>
                          {monthDates.map((d) => (
                            <td key={d} style={{ padding: 2, textAlign: "center" }}>
                              <AttendanceDayCell
                                compact
                                day={r.days?.[d]}
                                today={d === todayKey}
                                onClick={() => openDrawer({ id: r.user_id, name: r.name }, d)}
                              />
                            </td>
                          ))}
                          <td className="t-num t-muted text-xs">{minutesLabel(r.totals?.worked_minutes || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <AttendanceLegend />
              </>
            )
          )}

          {tab === "muster" && staffView && (
            <StaffMonthView
              user={staffView}
              month={month}
              onBack={() => setStaffView(null)}
              onDayClick={(d) => openDrawer({ id: staffView.user_id, name: staffView.name }, d.work_date)}
            />
          )}

          {tab === "day" && (
            dayApi.loading ? (
              <LoadingBlock rows={5} />
            ) : dayRows.length === 0 ? (
              <EmptyState icon="clock" title="Nothing here" hint="Pick a (past or current) date." />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>Status</th>
                    <th>First in</th>
                    <th>Last out</th>
                    <th className="t-num">Worked</th>
                    <th>Flags</th>
                    <th>Sessions</th>
                    <th style={{ width: 70 }} />
                  </tr>
                </thead>
                <tbody>
                  {dayRows.map((r) => {
                    const meta = statusMeta(r.status);
                    return (
                      <tr key={r.user_id}>
                        <td>
                          <span className="t-primary fw-500">{r.name}</span>
                          <div className="t-muted text-xs">{r.role === "admin" ? "Admin" : r.department || "—"}</div>
                        </td>
                        <td>
                          {meta ? (
                            <span className="badge" style={{ background: meta.soft, color: meta.color }}>
                              {r.status === "leave" && Number(r.leave_days) === 0.5 ? "½ leave" : meta.label}
                              {r.is_overridden ? " ✎" : ""}
                            </span>
                          ) : "—"}
                        </td>
                        <td>{timePart(r.first_in_at)}</td>
                        <td>{timePart(r.last_out_at)}</td>
                        <td className="t-num">{minutesLabel(r.worked_minutes)}</td>
                        <td className="text-xs">
                          {r.late ? <span style={{ color: "#f59e0b" }}>late {r.late_minutes}m </span> : null}
                          {r.missing_checkout ? <span style={{ color: "#ef4444" }}>no checkout</span> : null}
                          {!r.late && !r.missing_checkout ? <span className="t-muted">—</span> : null}
                        </td>
                        <td className="t-muted text-xs">
                          {(r.logs || []).length} slot{(r.logs || []).length === 1 ? "" : "s"}
                          {(r.logs || []).some((l) => l.in_geofence_status === "outside" || l.out_geofence_status === "outside") && (
                            <span className="badge badge-danger" style={{ marginLeft: 6 }}>outside</span>
                          )}
                        </td>
                        <td>
                          <button className="icon-btn" title="Inspect / edit" onClick={() => openDrawer({ id: r.user_id, name: r.name }, date)}>
                            <Icon name="edit" size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>

      <DayDrawer
        open={!!drawer}
        user={drawer?.user}
        date={drawer?.date}
        onClose={() => setDrawer(null)}
        onChanged={reloadAll}
      />
    </>
  );
}

function Kpi({ label, value, hint, loading }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{loading ? "…" : value ?? "—"}</div>
      {hint && <div className="kpi-delta t-muted">{hint}</div>}
    </div>
  );
}

/** One staff member's colour-coded month calendar (from the muster). */
function StaffMonthView({ user, month, onBack, onDayClick }) {
  const monthApi = useApi(() => userAttendanceMonth(user.user_id, month), [user.user_id, month]);
  const data = monthApi.data?.data;

  return (
    <>
      <div className="row gap-md" style={{ alignItems: "center", marginBottom: 10 }}>
        <button className="btn btn-secondary" onClick={onBack}>
          <Icon name="chevron-left" size={14} /> All staff
        </button>
        <div>
          <div className="t-primary fw-500">{user.name}</div>
          <div className="t-muted text-xs">
            {data?.shift ? `Shift: ${data.shift.name} · ${String(data.shift.start_time).slice(0, 5)}–${String(data.shift.end_time).slice(0, 5)}` : "No shift assigned"}
          </div>
        </div>
      </div>
      {monthApi.loading ? (
        <LoadingBlock rows={4} height={60} />
      ) : (
        <>
          <AttendanceMonthCalendar month={month} days={data?.days || []} onDayClick={onDayClick} />
          <AttendanceLegend />
        </>
      )}
    </>
  );
}

/** Day drill-down: summary, slots (admin CRUD), status override, audit trail. */
function DayDrawer({ open, user, date, onClose, onChanged }) {
  const [editSlot, setEditSlot] = useState(null); // null | 'new' | log object
  const [overrideOpen, setOverrideOpen] = useState(false);

  const monthKey = date ? date.slice(0, 7) : null;
  const monthApi = useApi(
    () => userAttendanceMonth(user.id, monthKey),
    [user?.id, monthKey, open],
    { immediate: !!(open && user && monthKey) },
  );
  const activity = useApi(
    () => dayActivity(user.id, date),
    [user?.id, date, open],
    { immediate: !!(open && user && date) },
  );

  if (!open) return null;

  const data = monthApi.data?.data;
  const day = (data?.days || []).find((d) => d.work_date === date);
  const logs = data?.logs?.[date] || [];
  const meta = day?.status ? statusMeta(day.status) : null;
  const activityRows = activity.data?.data?.data || [];

  const refresh = () => {
    monthApi.reload();
    activity.reload();
    onChanged?.();
  };

  const removeSlot = async (log) => {
    if (!window.confirm("Delete this check-in/out entry? The day will be recalculated.")) return;
    try {
      await deletePunchLog(log.id);
      showToast("success", "Entry deleted");
      refresh();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      wide
      title={`${user.name} — ${formatDate(date)}`}
      subtitle="Fix punches, override the day status, and see who changed what."
    >
      <div className="col gap-md">
        {monthApi.loading ? (
          <LoadingBlock rows={3} />
        ) : (
          <>
            {/* Summary */}
            <div className="row gap-sm" style={{ flexWrap: "wrap" }}>
              {meta && (
                <span className="badge" style={{ background: meta.soft, color: meta.color }}>
                  {day.status === "leave" && Number(day.leave_days) === 0.5 ? "½ leave" : meta.label}
                </span>
              )}
              <span className="badge badge-neutral">{minutesLabel(day?.worked_minutes || 0)} worked</span>
              {day?.late ? <span className="badge badge-warning">late {day.late_minutes}m</span> : null}
              {day?.shortfall_minutes > 0 ? <span className="badge badge-warning">short {minutesLabel(day.shortfall_minutes)}</span> : null}
              {day?.missing_checkout ? <span className="badge badge-danger">missing checkout</span> : null}
              {day?.is_overridden ? <span className="badge badge-neutral">overridden</span> : null}
            </div>

            {/* Slots */}
            <div className="field">
              <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <label className="field-label" style={{ marginBottom: 0 }}>Check-in / out entries</label>
                <button className="btn btn-secondary" onClick={() => setEditSlot("new")}>
                  <Icon name="plus" size={13} /> Add entry
                </button>
              </div>
              {logs.length === 0 ? (
                <div className="t-muted text-xs" style={{ marginTop: 6 }}>No punches on this day.</div>
              ) : (
                <table className="table" style={{ marginTop: 6 }}>
                  <thead>
                    <tr>
                      <th>In</th>
                      <th>Out</th>
                      <th>Location</th>
                      <th>Source</th>
                      <th style={{ width: 80 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((l) => (
                      <tr key={l.id}>
                        <td>{timePart(l.check_in_at)}</td>
                        <td>{l.check_out_at ? timePart(l.check_out_at) : <span className="badge badge-danger">open</span>}</td>
                        <td className="text-xs">
                          {geoBadge(l.in_geofence_status, "in")} {geoBadge(l.out_geofence_status, "out")}
                        </td>
                        <td className="t-muted text-xs">{l.source}</td>
                        <td>
                          <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                            <button className="icon-btn" title="Edit" onClick={() => setEditSlot(l)}>
                              <Icon name="edit" size={13} />
                            </button>
                            <button className="icon-btn" title="Delete" onClick={() => removeSlot(l)} style={{ color: "var(--danger)" }}>
                              <Icon name="trash" size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {editSlot && (
              <SlotForm
                userId={user.id}
                date={date}
                log={editSlot === "new" ? null : editSlot}
                onDone={() => { setEditSlot(null); refresh(); }}
                onCancel={() => setEditSlot(null)}
              />
            )}

            {/* Override */}
            <div className="field">
              <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <label className="field-label" style={{ marginBottom: 0 }}>Status override</label>
                {!overrideOpen && (
                  <button className="btn btn-secondary" onClick={() => setOverrideOpen(true)}>
                    <Icon name="edit" size={13} /> Override status
                  </button>
                )}
              </div>
              {day?.is_overridden && !overrideOpen && (
                <div className="t-muted text-xs" style={{ marginTop: 6 }}>
                  Pinned manually{day.override_note ? ` — “${day.override_note}”` : ""}. Recomputes keep it.
                </div>
              )}
              {overrideOpen && (
                <OverrideForm
                  userId={user.id}
                  date={date}
                  current={day?.status}
                  onDone={() => { setOverrideOpen(false); refresh(); }}
                  onCancel={() => setOverrideOpen(false)}
                />
              )}
            </div>

            {/* Audit trail */}
            <div className="field">
              <label className="field-label">Change history</label>
              {activity.loading ? (
                <LoadingBlock rows={2} />
              ) : activityRows.length === 0 ? (
                <div className="t-muted text-xs">No edits recorded for this day.</div>
              ) : (
                <div className="col gap-sm">
                  {activityRows.map((a) => (
                    <div key={a.id} style={{ borderLeft: "2px solid var(--line, #e2e8f0)", paddingLeft: 10 }}>
                      <div className="text-sm">
                        <span className="fw-500 t-primary">{a.causer || "System"}</span>{" "}
                        <span className="t-muted">{a.description.toLowerCase()}</span>
                      </div>
                      <div className="t-muted text-xs">{formatDateTime(a.created_at)}</div>
                      {a.changes?.length > 0 && (
                        <div className="row gap-sm text-xs" style={{ flexWrap: "wrap", marginTop: 3 }}>
                          {a.changes.map((c) => (
                            <span key={c.field} className="badge badge-neutral">
                              {c.field}: {fmtVal(c.old)} → {fmtVal(c.new)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}

function SlotForm({ userId, date, log, onDone, onCancel }) {
  const [inTime, setInTime] = useState(log ? timeValue(log.check_in_at) : "");
  const [outTime, setOutTime] = useState(log?.check_out_at ? timeValue(log.check_out_at) : "");
  const [outNextDay, setOutNextDay] = useState(log?.check_out_at ? log.check_out_at.slice(0, 10) > date : false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!inTime) return showToast("error", "Check-in time is required.");
    setSaving(true);
    try {
      const payload = {
        check_in_time: inTime,
        check_out_time: outTime || null,
        out_next_day: outNextDay,
      };
      if (log) {
        await updatePunchLog(log.id, payload);
        showToast("success", "Entry updated");
      } else {
        await addPunchLog(userId, { ...payload, work_date: date });
        showToast("success", "Entry added");
      }
      onDone?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ background: "var(--surface-2, #f8fafc)" }}>
      <div className="card-body col gap-md">
        <div className="t-primary text-sm fw-500">{log ? "Edit entry" : "Add entry"}</div>
        <div className="row gap-md" style={{ flexWrap: "wrap" }}>
          <div className="field" style={{ flex: 1, minWidth: 120 }}>
            <label className="field-label">Check-in</label>
            <input type="time" className="input" value={inTime} onChange={(e) => setInTime(e.target.value)} />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 120 }}>
            <label className="field-label">Check-out (optional)</label>
            <input type="time" className="input" value={outTime} onChange={(e) => setOutTime(e.target.value)} />
          </div>
        </div>
        <div className="row gap-sm">
          <input id="out-next-day" type="checkbox" checked={outNextDay} onChange={(e) => setOutNextDay(e.target.checked)} />
          <label htmlFor="out-next-day" className="text-sm" style={{ cursor: "pointer", marginBottom: 0 }}>
            Check-out is past midnight (next day)
          </label>
        </div>
        <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function OverrideForm({ userId, date, current, onDone, onCancel }) {
  const [status, setStatus] = useState(current || "full");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!note.trim()) return showToast("error", "A note explaining the override is required.");
    setSaving(true);
    try {
      await overrideDay(userId, date, { status, note: note.trim() });
      showToast("success", "Day status overridden");
      onDone?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ background: "var(--surface-2, #f8fafc)", marginTop: 6 }}>
      <div className="card-body col gap-md">
        <div className="row gap-md" style={{ flexWrap: "wrap" }}>
          <div className="field" style={{ flex: 1, minWidth: 140 }}>
            <label className="field-label">Status</label>
            <select className="select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="full">Present (full)</option>
              <option value="half">Half day</option>
              <option value="leave">Leave</option>
              <option value="absent">Absent</option>
              <option value="holiday">Holiday</option>
              <option value="weekly_off">Weekly off</option>
            </select>
          </div>
          <div className="field" style={{ flex: 2, minWidth: 200 }}>
            <label className="field-label">Why? (shown in the audit trail)</label>
            <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Worked from client site" />
          </div>
        </div>
        <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? "Saving…" : "Override"}</button>
        </div>
      </div>
    </div>
  );
}

/* ── helpers ─────────────────────────────────────────────────────────── */

function datesOfMonth(month) {
  const start = nowIst().year(Number(month.slice(0, 4))).month(Number(month.slice(5, 7)) - 1).startOf("month");
  return Array.from({ length: start.daysInMonth() }, (_, i) => start.add(i, "day").format("YYYY-MM-DD"));
}

function timePart(iso) {
  if (!iso) return "—";
  const parts = formatDateTime(iso).split("·");
  return parts[1]?.trim() || "—";
}

function timeValue(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" });
  } catch {
    return "";
  }
}

function geoBadge(status, label) {
  if (status === "inside") return <span className="badge badge-success">{label} inside</span>;
  if (status === "outside") return <span className="badge badge-danger">{label} outside</span>;
  return null;
}

function fmtVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  const s = String(v);
  // Datetimes render as local time; plain values pass through.
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s)) return formatDateTime(s);
  return s;
}
